<template>
  <div class="system-email-display">
    <div v-if="isLoading" class="loading-indicator">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <button 
      v-else-if="email" 
      @click="copyEmail" 
      class="email-btn"
      :title="'點擊複製 Email：' + email"
    >
      <i class="fas fa-envelope"></i>
      <span class="email-text">{{ email }}</span>
      <i class="fas fa-copy copy-icon"></i>
    </button>
    <div v-else class="no-email">
      <i class="fas fa-exclamation-triangle"></i>
      <span>未設定 Email</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { getSystemParameterPage } from '@/api/systemParameter.api.js';

const toast = useToast();
const email = ref('');
const isLoading = ref(true);

const loadSystemEmail = async () => {
  isLoading.value = true;
  try {
    // 使用分頁查詢搜尋 paramKey 為 'system.email' 的參數
    const queryRequest = { paramKey: 'system.email' };
    const pageable = { page: 0, size: 1 };
    const response = await getSystemParameterPage(queryRequest, pageable);
    
    if (response.content && response.content.length > 0) {
      email.value = response.content[0].paramValue;
    } else {
      email.value = '';
    }
  } catch (error) {
    console.error('載入系統 Email 失敗:', error);
    email.value = '';
  } finally {
    isLoading.value = false;
  }
};

const copyEmail = async () => {
  if (!email.value) return;
  
  try {
    await navigator.clipboard.writeText(email.value);
    toast.success(`已複製 Email：${email.value}`);
  } catch (error) {
    // 如果 Clipboard API 不可用，使用 fallback 方法
    try {
      const textArea = document.createElement('textarea');
      textArea.value = email.value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(`已複製 Email：${email.value}`);
    } catch (fallbackError) {
      console.error('複製失敗:', fallbackError);
      toast.error('複製失敗，請手動複製');
    }
  }
};

onMounted(() => {
  loadSystemEmail();
});
</script>

<style scoped>
.system-email-display {
  display: inline-flex;
  align-items: center;
}

.loading-indicator {
  color: #a0a0b0;
  font-size: 0.9em;
  padding: 8px;
}

.email-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: linear-gradient(145deg, rgba(139, 233, 253, 0.1), rgba(80, 200, 234, 0.1));
  border: 1px solid rgba(139, 233, 253, 0.3);
  border-radius: 5px;
  color: #8be9fd;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
}

.email-btn:hover {
  background: linear-gradient(145deg, rgba(139, 233, 253, 0.2), rgba(80, 200, 234, 0.2));
  border-color: rgba(139, 233, 253, 0.5);
  color: #b3f0ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(139, 233, 253, 0.2);
}

.email-btn:active {
  transform: translateY(0);
}

.email-text {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-icon {
  font-size: 0.8em;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.email-btn:hover .copy-icon {
  opacity: 1;
}

.no-email {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  color: #a0a0b0;
  font-size: 0.9em;
  background-color: rgba(40, 40, 50, 0.5);
  border: 1px solid rgba(80, 80, 90, 0.5);
  border-radius: 5px;
}

.no-email i {
  font-size: 0.8em;
  color: #ff8c69;
}

@media (max-width: 768px) {
  .email-text {
    max-width: 120px;
  }
}
</style>